// src/components/layout/Header.jsx
import React, {useMemo, useCallback, useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useDispatch, useSelector, shallowEqual} from "react-redux";
import {selectUnreadCount, selectTopUnread} from "../../store/selectors";
import {userActions} from "../../store/userAuthSlice";
import {notificationsActions} from "../../store/notificationsSlice";
import InitialsAvatar from "../../util/name_initial";
import {formattedDateTime} from "../../util/date";

// ✅ import your single Apollo client instance (from the adjusted App)
import {apolloClient} from "../../App";
import {useBranding} from "../../context/BrandingContext";
import useThemeColors from "../organization/portal/useThemeColors";
import useTranslation from "../../i18n/useTranslation";
import LanguageSelector from "./LanguageSelector";
import {GlobalStyles} from "../../constants/styles";

const MENU = {
    QUICK: "quick",
    NOTIFS: "notifs",
    PROFILE: "profile",
};

function typeToIcon(typeRaw) {
    const type = String(typeRaw || "").toLowerCase();
    if (type.includes("schedule")) return "fas fa-calendar-alt";
    if (type.includes("game")) return "fas fa-futbol";
    if (type.includes("training")) return "fas fa-dumbbell";
    if (type.includes("sanction")) return "fas fa-gavel";
    if (type.includes("payment")) return "fas fa-credit-card";
    if (type.includes("message") || type.includes("chat")) return "fas fa-comment-dots";
    return "fas fa-bell";
}

function getInitials(fullName) {
    const name = String(fullName || "").trim();
    if (!name) return "?";
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase())
        .join("");
}

function Header() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const colors = useThemeColors();
    const {isOrgPortal} = useBranding();
    const {t, language} = useTranslation();

    const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

    // Select only what we need (keeps rerenders lower)
    const user = useSelector((state) => state?.user?.auth?.user, shallowEqual);
    const profilesFromState = user?.profiles;

    // Notifications (Redux slice summary)
    const unreadCount = useSelector(selectUnreadCount);
    const topUnread = useSelector(selectTopUnread);

    const profilesList = useMemo(() => {
        return Array.isArray(profilesFromState) ? profilesFromState : [];
    }, [profilesFromState]);

    // ✅ Source of truth for UI: Redux profiles
    const activeProfile = useMemo(() => {
        if (!profilesList.length) return null;
        return profilesList.find((p) => p?.activeInWeb === true) || profilesList[0] || null;
    }, [profilesList]);

    const activeProfileName = useMemo(() => {
        if (!activeProfile) return "Account";
        return `${activeProfile.firstName ?? ""} ${activeProfile.lastName ?? ""}`.trim();
    }, [activeProfile]);

    const activeLogoUrl = activeProfile?.logoUrl || null;

    const [openMenu, setOpenMenu] = useState(null); // only one open
    const rootRef = useRef(null);

    // responsive small-screen detection
    const [isSmallScreen, setIsSmallScreen] = useState(() => {
        if (typeof window === "undefined") return false;
        return window.matchMedia("(max-width: 576px)").matches;
    });

    useEffect(() => {
        if (typeof window === "undefined") return;
        const mq = window.matchMedia("(max-width: 576px)");
        const handler = () => setIsSmallScreen(mq.matches);

        if (mq.addEventListener) mq.addEventListener("change", handler);
        else mq.addListener(handler);

        return () => {
            if (mq.removeEventListener) mq.removeEventListener("change", handler);
            else mq.removeListener(handler);
        };
    }, []);

    const closeMenus = useCallback(() => setOpenMenu(null), []);

    const toggleMenu = useCallback((menuKey) => {
        setOpenMenu((prev) => (prev === menuKey ? null : menuKey));
    }, []);

    // Close on outside click / touch
    useEffect(() => {
        const handler = (e) => {
            if (!openMenu) return;
            if (!rootRef.current) return;
            if (!rootRef.current.contains(e.target)) closeMenus();
        };
        document.addEventListener("mousedown", handler, true);
        document.addEventListener("touchstart", handler, true);
        return () => {
            document.removeEventListener("mousedown", handler, true);
            document.removeEventListener("touchstart", handler, true);
        };
    }, [openMenu, closeMenus]);

    // body scroll lock on mobile only
    useEffect(() => {
        if (!isSmallScreen) return;

        if (!openMenu) {
            const scrollY = document.body.getAttribute("data-kbola-scroll-y");
            document.body.classList.remove("kbola-no-scroll");
            document.body.style.top = "";
            if (scrollY) window.scrollTo(0, parseInt(scrollY, 10) || 0);
            document.body.removeAttribute("data-kbola-scroll-y");
            return;
        }

        const currentY = window.scrollY || 0;
        document.body.setAttribute("data-kbola-scroll-y", String(currentY));
        document.body.classList.add("kbola-no-scroll");
        document.body.style.top = `-${currentY}px`;
    }, [openMenu, isSmallScreen]);

    // iOS touch trap
    useEffect(() => {
        if (!isSmallScreen) return;
        if (!openMenu) return;

        let startY = 0;

        const onTouchStart = (e) => {
            startY = e.touches?.[0]?.clientY ?? 0;
        };

        const onTouchMove = (e) => {
            const el = e.target;
            const scroller = el?.closest?.(".kbola-menu-list");

            if (!scroller) {
                e.preventDefault();
                return;
            }

            const currentY = e.touches?.[0]?.clientY ?? 0;
            const deltaY = currentY - startY;

            const atTop = scroller.scrollTop <= 0;
            const atBottom =
                scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;

            if ((atTop && deltaY > 0) || (atBottom && deltaY < 0)) {
                e.preventDefault();
            }
        };

        document.addEventListener("touchstart", onTouchStart, {passive: false});
        document.addEventListener("touchmove", onTouchMove, {passive: false});

        return () => {
            document.removeEventListener("touchstart", onTouchStart);
            document.removeEventListener("touchmove", onTouchMove);
        };
    }, [openMenu, isSmallScreen]);

    // Keyboard escape
    useEffect(() => {
        const handler = (e) => {
            if (e.key === "Escape") closeMenus();
        };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [closeMenus]);

    const logoutHandler = useCallback(async () => {
        dispatch(userActions.logout());
        dispatch(notificationsActions.resetNotifications());
        closeMenus();

        // ✅ also clear Apollo cache to avoid stale UI
        try {
            await apolloClient.clearStore();
        } catch (e) {
        }

        navigate("/", {replace: true});
    }, [dispatch, navigate, closeMenus]);

    /**
     * ✅ PROFILE SWITCH (the key fix)
     * - Update Redux (activeInWeb)
     * - resetStore() => drops cache + refetches active queries
     * - always navigate to /management/organizations (replace)
     */
    const switchProfile = useCallback(
        async (id) => {
            const currentActiveId =
                profilesList.find((p) => p?.activeInWeb)?.id || profilesList?.[0]?.id;

            // no-op if same
            if (String(currentActiveId) === String(id)) {
                closeMenus();
                navigate("/management/organizations", {replace: true});
                return;
            }

            // 1) update redux (and localStorage inside reducer)
            dispatch(userActions.activeProfile(id));

            closeMenus();

            // 2) nuke/refetch Apollo cache so pages reload with new profileId variables
            try {
                await apolloClient.resetStore();
            } catch (e) {
                // if resetStore fails (rare), at least clear cache
                try {
                    await apolloClient.clearStore();
                } catch (_) {
                }
            }

            // 3) always go to organizations
            navigate("/management/organizations", {replace: true});
        },
        [dispatch, profilesList, closeMenus, navigate]
    );

    const goTo = useCallback(
        (path) => {
            closeMenus();
            navigate(path);
        },
        [navigate, closeMenus]
    );

    // Quick actions routes (adjust to your routes)
    const onNewOrganization = useCallback(() => goTo("/management/organizations"), [goTo]);
    const onAddVenue = useCallback(() => goTo("/management/venues"), [goTo]);
    const onPlans = useCallback(() => goTo("/subscription/plans"), [goTo]);
    const onInvoices = useCallback(() => goTo("/subscription/invoices"), [goTo]);
    const onNewSponsor = useCallback(() => goTo("/advertise/sponsor"), [goTo]);
    const onNewCampaign = useCallback(() => goTo("/advertise/campaign"), [goTo]);

    const openNotificationsPage = useCallback(() => goTo("/notifications"), [goTo]);

    const badgeText = useMemo(() => {
        if (!unreadCount || unreadCount <= 0) return null;
        return unreadCount > 99 ? "99+" : String(unreadCount);
    }, [unreadCount]);

    // Dropdown width control (keeps it from becoming huge)
    const notifMenuStyle = useMemo(() => {
        return isSmallScreen
            ? {width: "min(520px, calc(100vw - 24px))"}
            : {width: "420px", maxWidth: "calc(100vw - 24px)"};
    }, [isSmallScreen]);

    return (
        <nav
            ref={rootRef}
            className="main-header navbar navbar-expand navbar-white navbar-light"
            style={{backgroundColor: colors.primary, color: colors.textOnPrimary}}
        >
            {/* Mobile overlay */}
            {isSmallScreen && openMenu && (
                <div
                    className="kbola-dd-overlay"
                    role="presentation"
                    onClick={closeMenus}
                    aria-hidden="true"
                />
            )}

            {/* Left */}
            <ul className="navbar-nav">
                <li className="nav-item">
                    <a
                        className="nav-link"
                        data-widget="pushmenu"
                        href="#"
                        role="button"
                        aria-label="Toggle sidebar"
                        onClick={(e) => e.preventDefault()}
                        style={{color: colors.textOnPrimary}}
                    >
                        <i className="fas fa-bars"/>
                    </a>
                </li>
            </ul>

            {/* Right */}
            <ul className="navbar-nav ml-auto kbola-header-right">
                {/* Quick actions */}
                <li className={`nav-item kbola-dd ${openMenu === MENU.QUICK ? "is-open" : ""}`}>
                    <button
                        type="button"
                        className="nav-link kbola-icon-btn kbola-dd-trigger"
                        aria-label={t('header.quickActions.title')}
                        title={t('header.quickActions.title')}
                        aria-expanded={openMenu === MENU.QUICK}
                        onClick={() => toggleMenu(MENU.QUICK)}
                        style={{color: colors.textOnPrimary}}
                    >
                        <i className="fas fa-bolt"  style={{fontSize: "20px"}}/>
                    </button>

                    <div
                        className={`kbola-dd-menu dropdown-menu kbola-menu kbola-menu-compact ${
                            openMenu === MENU.QUICK ? "show" : ""
                        }`}
                    >
                        <div className="kbola-menu-head">
                            <div className="kbola-menu-title">{t('header.quickActions.title')}</div>
                            <div className="kbola-menu-subtitle">{t('header.quickActions.subtitle')}</div>
                        </div>

                        <div className="kbola-divider"/>

                        <div className="kbola-menu-body">
                            <div className="kbola-menu-list">
                                <button type="button" className="kbola-menu-item" onClick={onNewOrganization}>
                  <span className="kbola-qa-icon">
                    <i className="fas fa-building"/>
                  </span>
                                    <span className="kbola-qa-text">{t('header.quickActions.newOrg')}</span>
                                </button>

                                <button type="button" className="kbola-menu-item" onClick={onAddVenue}>
                  <span className="kbola-qa-icon">
                    <i className="fas fa-map-marker-alt"/>
                  </span>
                                    <span className="kbola-qa-text">{t('header.quickActions.addVenue')}</span>
                                </button>

                                <div className="kbola-divider"/>

                  {/*              <button type="button" className="kbola-menu-item" onClick={onNewSponsor}>*/}
                  {/*<span className="kbola-qa-icon">*/}
                  {/*  <i className="fas fa-user-shield"/>*/}
                  {/*</span>*/}
                  {/*                  <span className="kbola-qa-text">{t('header.quickActions.newSponsor')}</span>*/}
                  {/*              </button>*/}

                  {/*              <button type="button" className="kbola-menu-item" onClick={onNewCampaign}>*/}
                  {/*<span className="kbola-qa-icon">*/}
                  {/*  <i className="fas fa-bullseye"/>*/}
                  {/*</span>*/}
                  {/*                  <span className="kbola-qa-text">{t('header.quickActions.newCampaign')}</span>*/}
                  {/*              </button>*/}
                            </div>
                        </div>
                    </div>
                </li>

                {/* Notifications */}
                <li className={`nav-item kbola-dd ${openMenu === MENU.NOTIFS ? "is-open" : ""}`} style={{marginRight: "20px"}}>
                    <button
                        type="button"
                        className="nav-link kbola-icon-btn kbola-dd-trigger"
                        aria-label="Notifications"
                        title="Notifications"
                        aria-expanded={openMenu === MENU.NOTIFS}
                        onClick={() => toggleMenu(MENU.NOTIFS)}
                        style={{
                            color: colors.textOnPrimary,
                            position: "relative",           // 👈 so the badge is relative to this
                        }}
                    >
                        <i
                            className="far fa-bell"
                            style={{fontSize: "20px"}}
                        />
                        {badgeText && (
                            <span
                                className="badge navbar-badge"
                                style={{
                                    backgroundColor: isOrgPortal
                                        ? colors.secondary
                                        : GlobalStyles.colors.brandOrange,
                                    color: colors.textOnSecondary,
                                    position: "absolute",
                                    top: "-2px",   // 👈 move up
                                    right: "-2px", // 👈 move right
                                    minWidth: "20px",
                                    height: "20px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "11px",
                                    fontWeight: 900,
                                    borderRadius: "50%",
                                }}
                            >
      {badgeText}
    </span>
                        )}
                    </button>

                    <div
                        style={notifMenuStyle}
                        className={`kbola-dd-menu dropdown-menu kbola-menu kbola-menu-notifications ${
                            openMenu === MENU.NOTIFS ? "show" : ""
                        }`}
                    >
                        <div className="kbola-menu-head">
                            <div className="kbola-menu-title">{t('header.notifications.title')}</div>
                            <div className="kbola-menu-subtitle">
                                {unreadCount > 0 ? `${unreadCount} ${t('header.notifications.unread')}` : t('header.notifications.subtitle')}
                            </div>
                        </div>

                        <div className="kbola-divider"/>

                        <div className="kbola-menu-body">
                            <div className="kbola-menu-list kbola-notif-list">
                                {topUnread.length === 0 ? (
                                    <div className="kbola-empty">
                                        <div className="kbola-empty-title">{t('header.notifications.noUnread')}</div>
                                        <div className="kbola-empty-sub">{t('header.notifications.info')}
                                        </div>
                                    </div>
                                ) : (
                                    topUnread.map((row) => {
                                        const notif = row?.notification;
                                        const title = notif?.title || "Notification";
                                        const message = notif?.message || "";
                                        const senderName = notif?.sender?.fullName || "AngeonX Sports";
                                        const createdAt = notif?.createdAt || null;
                                        const createdAtLabel = createdAt
                                            ? formattedDateTime(createdAt, timezone, language, "LLL d ',' h:mm a")
                                            : "";

                                        const isUnread = !row?.isRead;
                                        const initials = getInitials(senderName);
                                        const iconClass = typeToIcon(notif?.type);

                                        return (
                                            <button
                                                key={row?.id}
                                                type="button"
                                                className={`kbola-notif-row ${isUnread ? "is-unread" : ""}`}
                                                onClick={openNotificationsPage}
                                            >
                                                <span className="kbola-notif-accent" aria-hidden="true" style={{
                                                    backgroundColor: colors.secondary,
                                                }}/>

                                                <div className="kbola-notif-avatar" aria-hidden="true" style={{
                                                    backgroundColor: colors.secondary,
                                                    color: colors.textOnSecondary,
                                                }}>
                                                    {initials}
                                                </div>

                                                <div className="kbola-notif-content">
                                                    <div className="kbola-notif-topline">
                                                        <div className="kbola-notif-titlewrap">
                                                            <i className={`kbola-notif-typeicon ${iconClass}`}
                                                               aria-hidden="true"/>
                                                            <span className="kbola-notif-title-text">{title}</span>
                                                        </div>

                                                        <div className="kbola-notif-rightmeta">
                                                            {isUnread && <span className="kbola-notif-unread-dot"
                                                                               aria-hidden="true" style={{
                                                                                   backgroundColor: colors.secondary,
                                                            }}/>}
                                                        </div>
                                                    </div>

                                                    {message ? (
                                                        <div className="kbola-notif-message" title={message}>
                                                            {message}
                                                        </div>
                                                    ) : null}

                                                    <div className="kbola-notif-foot">
                                                        <span className="kbola-notif-sender">{senderName}</span>
                                                        {createdAtLabel ? (
                                                            <>
                                                                <span className="kbola-notif-sep">•</span>
                                                                <span
                                                                    className="kbola-notif-time">{createdAtLabel}</span>
                                                            </>
                                                        ) : null}
                                                    </div>
                                                </div>

                                                <span className="kbola-notif-chevron" aria-hidden="true">
                          <i className="fas fa-angle-right"/>
                        </span>
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        <div className="kbola-menu-footer">
                            <div className="kbola-divider"/>
                            <button
                                type="button"
                                className="kbola-menu-item kbola-view-more"
                                onClick={openNotificationsPage}
                            >
                <span className="kbola-qa-icon">
                  <i className="fas fa-inbox"/>
                </span>
                                <span className="kbola-qa-text">{t('header.notifications.viewMore')}</span>
                            </button>
                        </div>
                    </div>
                </li>

                {/* Language */}
                <li className="nav-item d-flex align-items-center px-1">
                    <LanguageSelector variant="desktop" />
                </li>

                {/* Profile */}
                <li className={`nav-item kbola-dd ${openMenu === MENU.PROFILE ? "is-open" : ""}`}>
                    <button
                        type="button"
                        className="nav-link kbola-account-trigger kbola-dd-trigger"
                        aria-label="Account menu"
                        aria-expanded={openMenu === MENU.PROFILE}
                        onClick={() => toggleMenu(MENU.PROFILE)}
                    >
            <span className="kbola-account-inline">
              {activeLogoUrl ? (
                  <img src={activeLogoUrl} alt={activeProfileName} className="kbola-avatar-img"/>
              ) : (
                  <span className="kbola-avatar-fallback">
                  <InitialsAvatar
                      name={activeProfileName}
                      size={38}
                      backgroundColor={colors.secondary}
                      textColor={colors.textOnSecondary}
                  />
                </span>
              )}

                <span className="kbola-account-name d-none d-md-inline"
                      style={{color: colors.textOnPrimary}}>{activeProfileName}</span>
              <i className="fas fa-angle-down kbola-account-chevron d-none d-md-inline"
                 style={{color: colors.textOnPrimary}}/>
            </span>
                    </button>

                    <div
                        className={`kbola-dd-menu dropdown-menu kbola-menu kbola-menu-profiles ${
                            openMenu === MENU.PROFILE ? "show" : ""
                        }`}
                    >
                        <div className="kbola-menu-head">
                            <div className="kbola-menu-title">{t('header.switchProfile.title')}</div>
                            <div className="kbola-menu-subtitle">
                                {profilesList.length} {t('header.switchProfile.profile')}{profilesList.length === 1 ? "" : "s"}
                            </div>
                        </div>

                        <div className="kbola-divider"/>

                        <div className="kbola-menu-body">
                            <div className="kbola-menu-list">
                                {profilesList.map((p) => {
                                    const name = `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim() || "Profile";
                                    const isActive = !!p.activeInWeb;
                                    const logoUrl = p.logoUrl || null;

                                    return (
                                        <button
                                            key={p.id}
                                            type="button"
                                            className={`kbola-menu-item kbola-profile-item ${isActive ? "is-active" : ""}`}
                                            onClick={() => switchProfile(p.id)}
                                        >
                                            {logoUrl ? (
                                                <img src={logoUrl} alt={name} className="kbola-profile-img"/>
                                            ) : (
                                                <InitialsAvatar
                                                    name={name}
                                                    size={40}
                                                    backgroundColor={colors.secondary}
                                                    textColor={colors.textOnSecondary}
                                                />
                                            )}

                                            <div className="kbola-profile-meta" style={{marginLeft: 10}}>
                                                <div className="kbola-profile-name">{name}</div>
                                                <div className="kbola-profile-hint">
                                                    {isActive ? t('header.switchProfile.active') : t('header.switchProfile.switchProfile')}
                                                </div>
                                            </div>

                                            {isActive ? (
                                                <span className="kbola-pill" style={{
                                                    backgroundColor: colors.secondary,
                                                    color: colors.textOnSecondary,
                                                }}>{t('header.switchProfile.activeBadge')}</span>
                                            ) : (
                                                <span className="kbola-profile-action">{t('header.switchProfile.switch')}</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="kbola-menu-footer">
                            <div className="kbola-divider"/>
                            <div className="kbola-menu-title">{t('header.switchProfile.subscriptions')}</div>
                            <button type="button" className="kbola-menu-item" onClick={onPlans}>
                                <span className="kbola-profile-icon"><i className="fas fa-star"/></span>
                                <span className="kbola-qa-text">{t('header.switchProfile.plans')}</span>
                            </button>
                            <button type="button" className="kbola-menu-item" onClick={onInvoices}>
                                <span className="kbola-profile-icon"><i className="fas fa-file-invoice"/></span>
                                <span className="kbola-qa-text">{t('header.switchProfile.invoices')}</span>
                            </button>
                            <div className="kbola-divider"/>
                            <button
                                type="button"
                                className="kbola-menu-item kbola-logout-item"
                                onClick={logoutHandler}
                            >
                <span className="kbola-profile-icon">
                  <i className="fas fa-sign-out-alt"/>
                </span>
                                <span className="kbola-qa-text" style={{fontWeight: 900}}>
                  {t('header.switchProfile.logout')}
                </span>
                            </button>
                        </div>
                    </div>
                </li>
            </ul>
        </nav>
    );
}

export default Header;
